/**
 * Pure scene mappers: Features in, a typed SceneIntent out. No state, no DOM,
 * no audio. Anything that needs memory (hysteresis, angular history, dwell)
 * lives in adapters.ts and calls these.
 *
 * The experiment config enters as an explicit parameter (defaulting to the
 * live one) so a test can pin either Gate 8 arm and the function stays a pure
 * function of its arguments.
 */

import { cutoffHz } from '../audio/common/pitch';
import { STROKE_ORDER } from '../music/talas';
import { AMAX, AMIN, betaFromContinuous, betaFromRung } from '../music/warble';
import { type ExperimentConfig, getExperiments } from './config';
import type {
  ArpIntent, BassIntent, BreaksIntent, FeatureFrame, ScratchIntent, TalaIntent, WarbleIntent,
} from './types';

const clamp = (lo: number, hi: number, v: number): number => (v < lo ? lo : v > hi ? hi : v);

/**
 * The cutoff axis — Gate 8's experiment switch, resolved per frame from config.
 *
 *   'spread'   option A: cutoff decoupled from the trigger axis.
 *   'aperture' option B/C: the coupled articulation. Aperture is a CLOSING
 *              measure, so it is inverted — closing toward the trigger darkens
 *              the note, which is the coupling under test, stated rather than
 *              accidental.
 *
 * Full doc range from either axis; the per-scene sub-ranges that used to live
 * here made the two scenes disagree with each other and with the doc.
 */
export function cutoffFrom(f: FeatureFrame, cfg: ExperimentConfig = getExperiments()): number {
  const axis = cfg.cutoffSource === 'spread' ? f.expr.spread : 1 - f.expr.aperture;
  return cutoffHz(clamp(0, 1, axis), cfg.cutoffLoHz, cfg.cutoffHiHz);
}

/**
 * Amplitude gating IS the rest state (gesture-vocabulary.md, "Rest state").
 * The bottom deadzone must reach true zero — a rest state that still hums is
 * not a rest state.
 */
export function gatedLevel(height: number, deadzone = 0.12): number {
  if (height <= deadzone) return 0;
  const top = 1 - deadzone;
  return clamp(0, 1, (height - deadzone) / (top - deadzone));
}

/** Roll → Q 1..12, the vocabulary's resonance row. */
export const rollToQ = (roll: number): number => 1 + clamp(0, 1, roll) * 11;

export function mapBass(
  f: FeatureFrame,
  opts: { trigger: boolean; accent: boolean; degree: number; octave: number },
  cfg: ExperimentConfig = getExperiments(),
): BassIntent {
  return {
    kind: 'bass',
    cutoffHz: cutoffFrom(f, cfg),
    resonance: rollToQ(f.expr.roll),
    drive: clamp(0, 1, 0.2 + f.expr.aperture * 0.5),
    level: gatedLevel(f.expr.height),
    degree: opts.degree,
    octave: opts.octave,
    trigger: opts.trigger,
    accent: opts.accent,
  };
}

export function mapArp(
  f: FeatureFrame,
  opts: { degree: number; running: boolean; octaves: number },
  cfg: ExperimentConfig = getExperiments(),
): ArpIntent {
  return {
    kind: 'arp',
    cutoffHz: cutoffFrom(f, cfg),
    resonance: rollToQ(f.expr.roll),
    level: gatedLevel(f.expr.height),
    degree: opts.degree,
    // Quantized from spread by the adapter, WITH band hysteresis — a raw
    // Math.round here is exactly the boundary flap the vocabulary doc forbids.
    octaves: opts.octaves,
    gate: 0.2 + (1 - clamp(0, 1, f.expr.aperture)) * 0.7,
    running: opts.running,
  };
}

export function mapBreaks(
  f: FeatureFrame,
  opts: { selectedSlice: number; triggerSlice: number; stutter: boolean },
): BreaksIntent {
  return {
    kind: 'breaks',
    level: gatedLevel(f.expr.height),
    // spread rides the slice rate over an octave either side of unity
    rate: Math.pow(2, (clamp(0, 1, f.expr.spread) - 0.5) * 2 * 0.5),
    selectedSlice: opts.selectedSlice,
    triggerSlice: opts.triggerSlice,
    stutter: opts.stutter,
  };
}

export function mapScratch(f: FeatureFrame, opts: { rate: number; cut: boolean }): ScratchIntent {
  return {
    kind: 'scratch',
    rate: opts.rate,
    cut: opts.cut,
    // Amplitude gates everything downstream — the scratch scene included. The
    // old mapping (present ? 1 : 0.8) left the scene with no rest state at
    // all, and was louder with a hand than without one either way.
    level: gatedLevel(f.expr.height),
  };
}

export function mapTala(
  f: FeatureFrame,
  opts: { running: boolean; strike: boolean; degree: number },
): TalaIntent {
  // Height picks the stroke, exactly as it picks the degree in Bass — one
  // selection gesture across the instrument. The vocabulary quantizes into
  // this scene's 8 steps (Scene.selectionSteps), so degree is already 0..7;
  // the clamp only guards a stale degree arriving mid scene-switch.
  const idx = Math.max(0, Math.min(STROKE_ORDER.length - 1, opts.degree));
  return {
    kind: 'tala',
    level: gatedLevel(f.expr.height),
    cents: Math.round((clamp(0, 1, f.expr.roll) - 0.5) * 100),
    velocity: 0.5 + clamp(0, 1, f.expr.spread) * 0.6,
    running: opts.running,
    strike: opts.strike,
    stroke: STROKE_ORDER[idx],
  };
}

export type WarbleMode = 'perch' | 'plane';

export function mapWarble(
  f: FeatureFrame,
  opts: { mode: WarbleMode; degree: number; fallbackBeta: number; fist: boolean; trigger: boolean },
): WarbleIntent {
  // The physical rest state: gatedLevel's deadzone maps to AMIN, which sits
  // below the phonation threshold at every beta — dropping the hand silences
  // the MODEL, no gain gating anywhere in this scene.
  const alpha = AMIN + gatedLevel(f.expr.height) * (AMAX - AMIN);
  // The pitch axis. No pitch hand (pad play, or one-hand camera): the patch's
  // baseline beta holds, so the scene stays playable one-handed.
  const p = f.pitch;
  const beta = p?.present && p.confident
    ? (opts.mode === 'perch' ? betaFromRung(opts.degree) : betaFromContinuous(p.height))
    : opts.fallbackBeta;
  return {
    kind: 'warble',
    alpha,
    beta,
    // The rim: overblow is a global act on whatever is sounding, not a patch.
    fold: opts.fist ? 2.4 : 0,
    grit: clamp(0, 1, Math.abs(f.expr.roll - 0.5) * 2) * 0.6,
    sounding: f.expr.present,
    rearticulate: opts.trigger,
  };
}
